/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import {
  FC,
  Fragment,
  useState,
  useEffect,
  useCallback,
  useRef,
  ImgHTMLAttributes,
} from "react"
import { Skeleton } from "@duxton-web/ui"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"

interface ImageWithFetchRetryProps
  extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "onError"> {
  /** Image source URL */
  src: string
  width: number
  height: number
}

/**
 * Image component with fetch retry.
 * When the image fails to load, attempts to fetch it directly
 * and converts it to a blob URL as an alternative loading method.
 *
 * Usage:
 * ```tsx
 * <ImageWithFetchRetry
 *   src={imageUrl}
 *   width={200}
 *   height={200}
 *   alt="description"
 *   className="my-image"
 * />
 * ```
 */
export const ImageWithFetchRetry: FC<ImageWithFetchRetryProps> = ({
  src,
  width,
  height,
  ...imgProps
}) => {
  const [imageSrc, setImageSrc] = useState(src)
  const [isLoading, setIsLoading] = useState(false)
  const blobUrlRef = useRef<string | null>(null)
  const fetchingSrcRef = useRef<string | null>(null)

  useEffect(() => {
    setImageSrc(src)
    setIsLoading(false)

    // Cleanup: revoke blob URL and reset fetch tracking to prevent memory leaks and race conditions
    return () => {
      fetchingSrcRef.current = null

      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [src])

  const handleError = useCallback(async (srcToFetch: string) => {
    // Only try fetch once per src
    if (fetchingSrcRef.current === srcToFetch || !srcToFetch) {
      return
    }

    // Check if feature flag is enabled
    const isEnabled = getGrabXValueFromLocalStorage<boolean>(
      MerchantPortalFeatureFlag.portalEnableImageFetchRetry,
    )
    if (!isEnabled) {
      return
    }

    fetchingSrcRef.current = srcToFetch
    setIsLoading(true)

    try {
      const response = await fetch(srcToFetch)

      // Only update if fetch succeeded and this src is still current
      // Note: No need to revoke previous blob URL here - the useEffect cleanup handles it when src changes
      if (response.ok && fetchingSrcRef.current === srcToFetch) {
        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        blobUrlRef.current = blobUrl
        setImageSrc(blobUrl)
      }
    } catch {
      // Keep using the original src
    } finally {
      // Only update loading state if this src is still current
      if (fetchingSrcRef.current === srcToFetch) {
        setIsLoading(false)
      }
    }
  }, [])

  if (isLoading) {
    return (
      <div data-testid="img-loading-skeleton">
        <Skeleton.Node
          active
          css={css`
            &.dui-skeleton {
              display: block;
            }
          `}
          style={{ width, height }}
        >
          <Fragment />
        </Skeleton.Node>
      </div>
    )
  }

  return (
    <img
      {...imgProps}
      src={imageSrc}
      width={width}
      height={height}
      onError={() => handleError(src)}
    />
  )
}
